import { Component, inject, viewChild } from '@angular/core';
import { ReceiptCodeDatasourceService } from '../receipt-code-datasource.service';
import { IReceiptCodeQuery, ReceiptCode } from 'src/app/api/api-campaigns.service.generated';
import { ReceiptCodeFiltersComponent } from '../receipt-code-filters/receipt-code-filters.component';
import { ColumnData, NgxDataTableComponent } from 'wecheer-common';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter } from 'rxjs';

@Component({
  selector: 'app-receipt-code-grid',
  templateUrl: './receipt-code-grid.component.html',
  styleUrls: ['./receipt-code-grid.component.scss'],
  imports: [
    ReceiptCodeFiltersComponent,
    NgxDataTableComponent
  ]
})
export class ReceiptCodeGridComponent {
  private datasource = inject(ReceiptCodeDatasourceService);

  readonly filtersPanel = viewChild<ReceiptCodeFiltersComponent>('filtersPanel');

  private scrollThreshold = 700;
  readonly pageLimit = 10;
  rowHeight = 90;
  columns: ColumnData[] = [
    { key: 'id', label: 'Id', sortable: false },
    { key: 'customerCode', label: 'Customer code', sortable: false },
    { key: 'invoiceNumber', label: 'Invoice number', sortable: false },
    { key: 'totalQuantity', label: 'Quantity', sortable: false, colWidth: 50 },
    { key: 'receiptDate', label: 'Receipt date', format: { type: 'date' }, sortable: false },
    { key: 'scanDate', label: 'Scan date', format: { type: 'date' }, sortable: false },
    { key: 'userId', label: 'User Id', sortable: false },
    { key: 'campaignId', label: 'Campaign Id', sortable: false },
    { key: 'cardId', label: 'Card Id', sortable: false }
  ];

  filter: IReceiptCodeQuery;
  receiptCodesList: ReceiptCode[];
  isFirstRequest: boolean;
  isLoading: boolean;

  hasMoreData = true;
  fetchMore$ = new BehaviorSubject<any>(null);

  constructor() {
    // Set up scroll-based pagination
    this.setupScrollPagination();
  }

  private setupScrollPagination() {
    this.fetchMore$
      .pipe(
        filter(event => event !== null),
        debounceTime(100),
        distinctUntilChanged()
      )
      .subscribe((scrollY) => {
        this.handleScrollEvent(scrollY);
      });
  }

  private handleScrollEvent(scrollY: any) {
    if (this.isLoading || !this.hasMoreData) {
      return;
    }

    const atBottom = this.receiptCodesList.length * this.rowHeight - scrollY < this.scrollThreshold;

    if (atBottom) {
      this.loadMoreData();
    }
  }

  private async loadMoreData() {
    if (!this.hasMoreData || this.isLoading) {
      return;
    }

    try {
      // Call the datasource getNextPage method
      this.datasource.getNextPage().subscribe({
        next: (response: any) => {
          if (response && response.receiptCodes && response.receiptCodes.length > 0) {

            // Check if there's more data available (assuming if we get less than pageLimit, no more data)
            this.hasMoreData = response.receiptCodes.length >= this.pageLimit;
          } else {
            // No more data available
            this.hasMoreData = false;
          }
        },
        error: () => {
          this.hasMoreData = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    } catch (error) {
      this.isLoading = false;
    }
  }

  ngAfterContentChecked(): void {
    this.receiptCodesList = this.datasource.receiptCodes;
    this.isFirstRequest = this.datasource.isFirstRequest;
    this.isLoading = this.datasource.isLoading;
  }

  openFilters() {
    this.filtersPanel().open();
  }

  onSearch(f: IReceiptCodeQuery) {
    this.filter = f;
    this.datasource.onSearch(f);
  }

  onScroll(event: any) {
    this.fetchMore$.next(event);
  }
}

